import { DiscountsType } from 'components/Modals/EditDiscountModal/EditDiscountModal';
import { IFormattedPricingItem } from '../hooks';

export const updatePricingWithDiscount = (
  pricing: Record<string, any>,
  key: string,
  discountValue: string | null,
  discountType: DiscountsType
): Record<string, any> => {
  if (!pricing || !pricing[key]) {
    return pricing;
  }

  // If it's null then we want to reset the discount
  if (discountValue === null) {
    return {
      ...pricing,
      [key]: {
        ...pricing[key],
        custom_discount: null,
        custom_amount: null,
      },
    };
  }

  const parsedDiscount = Number(discountValue);

  return {
    ...pricing,
    [key]: {
      ...pricing[key],
      custom_discount:
        discountType === DiscountsType.PERCENTAGE && !isNaN(parsedDiscount)
          ? parsedDiscount / 100
          : null,
      custom_amount:
        discountType === DiscountsType.AMOUNT && !isNaN(parsedDiscount)
          ? parsedDiscount
          : null,
    },
  };
};

export const updateAllPricingWithDiscount = (
  pricing: Record<string, any>,
  discountValue: string | null,
  discountType: DiscountsType
): Record<string, any> => {
  const parsedDiscount = Number(discountValue);

  return Object.keys(pricing).reduce((acc, key) => {
    if (key.endsWith('_gross')) {
      acc[key] = pricing[key];
      return acc;
    }

    acc[key] = {
      ...pricing[key],
      custom_discount:
        discountType === DiscountsType.PERCENTAGE && !isNaN(parsedDiscount)
          ? parsedDiscount / 100
          : null,
      custom_amount:
        discountType === DiscountsType.AMOUNT && !isNaN(parsedDiscount)
          ? parsedDiscount
          : null,
    };

    return acc;
  }, {} as Record<string, any>);
};

export const resetAllDiscounts = (
  pricing: Record<string, any>
): Record<string, any> => {
  return Object.keys(pricing).reduce((acc, key) => {
    if (key.endsWith('_gross')) {
      acc[key] = pricing[key];
      return acc;
    }

    acc[key] = {
      ...pricing[key],
      custom_discount: null,
      custom_amount: null,
    };

    return acc;
  }, {} as Record<string, any>);
};

export const getDiscountDisplay = (
  netItem: IFormattedPricingItem['netItem']
): string | null => {
  // Case 1: Custom discount with percentage
  if (netItem.custom_amount != null || netItem.custom_discount != null) {
    return netItem.discount_percentage && netItem.discount_percentage > 0
      ? `${netItem.discount_percentage}%`
      : null;
  }
  // Case 2: Regular discount
  if (netItem.discount != null && netItem.discount > 0) {
    return `${netItem.discount}%`;
  }

  return null;
};

export const hasAnyDiscounts = (prices: IFormattedPricingItem[]): boolean => {
  return prices.some((price) => {
    return (
      price.netItem.custom_amount != null ||
      price.netItem.custom_discount != null ||
      (price.netItem.discount != null && price.netItem.discount > 0)
    );
  });
};

// Only on intent configuration
// Helper to reset discounts for lines that have changed due to property changes
// Only resets once per line to allow users to set new discounts after property changes
export const resetDiscountsForChangedLines = (
  pricing: Record<string, any>,
  previousPricing: Record<string, any> | null,
  resetDiscountLines: Set<string>
): { updatedPricing: Record<string, any>; newResetLines: Set<string> } => {
  if (!previousPricing) {
    return { updatedPricing: pricing, newResetLines: new Set() };
  }

  const updatedPricing = { ...pricing };
  const newResetLines = new Set(resetDiscountLines);

  // Find lines where the gross value changed (indicating property change)
  for (const key of Object.keys(pricing)) {
    if (key.endsWith('_gross')) continue; // Skip gross items

    const currentItem = pricing[key];

    // Get the corresponding gross item to check for gross price changes
    // Keys are like P_Operation (net) and P_Operation_gross (gross)
    const grossKey = key + '_gross';
    const currentGrossItem = pricing[grossKey];
    const previousGrossItem = previousPricing[grossKey];

    // Check if the gross value changed (indicating property change)
    const grossValueChanged =
      previousGrossItem &&
      currentGrossItem &&
      previousGrossItem.value !== currentGrossItem.value;

    // Check if this line has custom discounts
    const hasCustomDiscount =
      currentItem.custom_discount != null || currentItem.custom_amount != null;

    const alreadyReset = resetDiscountLines.has(key);

    // If gross value changed, has custom discount, and hasn't been reset yet
    if (grossValueChanged && hasCustomDiscount && !alreadyReset) {
      // Reset the discount
      updatedPricing[key] = {
        ...currentItem,
        custom_discount: null,
        custom_amount: null,
      };
      newResetLines.add(key);
    }
  }

  return { updatedPricing, newResetLines };
};
